import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { EntityStatus, GuideSourceFormat } from "../types";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";
import { getTenantId } from "../utils/jwtUtils";
import { useAuth } from "../contexts/AuthContext";
import { isProductionSupportAdmin, isProductManager } from "../utils/appRoles";
import {
  GUIDE_FILE_ACCEPT,
  inferDocumentDescription,
  inferDocumentName,
  readGuideFile,
} from "../utils/guideFileReader";
import {
  OPERATOR_DOCUMENT_ENTITY_CLASSES,
  OPERATOR_DOCUMENT_CLASS_LABELS,
  type OperatorDocumentEntityClass,
} from "../utils/operatorDocuments";

interface AddDocumentProps {
  returnPath?: string;
  returnSearch?: string;
}

const AddDocument: React.FC<AddDocumentProps> = ({
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const lockOperatorTypes =
    isProductionSupportAdmin(roles) && !isProductManager(roles);
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "documents"));

  const tenantId = getTenantId() ?? "";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);
  const [entityClass, setEntityClass] = useState<OperatorDocumentEntityClass>("sop");
  const [content, setContent] = useState("");
  const [sourceFileName, setSourceFileName] = useState<string | undefined>();
  const [importing, setImporting] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setErrorMessage(null);
    try {
      const result = await readGuideFile(file);
      setContent(result.text);
      setSourceFileName(result.fileName);
      if (!name.trim()) setName(inferDocumentName(file.name, result.text, result.title));
      if (!description.trim()) setDescription(inferDocumentDescription(result.text, file.name));
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!tenantId) {
      setErrorMessage("Tenant context is required");
      return;
    }
    if (!entityClass) {
      setErrorMessage("Document type is required");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createDocument({
        name,
        description,
        tenantId,
        status,
        entityClass,
        content: content.trim() || undefined,
        sourceFileName,
        sourceFormat: content.trim() ? GuideSourceFormat.MARKDOWN : undefined,
      });
      setSuccessOpen(true);
      setTimeout(() => {
        goBack();
      }, 800);
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to create document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    goBack();
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add Document"
        pageDescription="Create a catalog document. Operator types are SOP, runbook, and help guide. Binary uploads are converted to Markdown first."
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: 720,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={3}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Document type"
              value={entityClass}
              onChange={(e) =>
                setEntityClass(e.target.value as OperatorDocumentEntityClass)
              }
              fullWidth
              required
              helperText={
                lockOperatorTypes
                  ? "Production support admin can author SOP, runbook, and help-guide documents."
                  : "Canonical operator subtypes: sop, runbook, help-guide."
              }
            >
              {OPERATOR_DOCUMENT_ENTITY_CLASSES.map((id) => (
                <MenuItem key={id} value={id}>
                  {OPERATOR_DOCUMENT_CLASS_LABELS[id]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EntityStatus)}
              fullWidth
            >
              {Object.values(EntityStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Button component="label" variant="outlined" disabled={importing}>
                {importing ? "Converting…" : "Import file (text or PDF/Word/PPT/image)"}
                <input
                  type="file"
                  hidden
                  accept={GUIDE_FILE_ACCEPT}
                  onChange={handleImport}
                />
              </Button>
              {sourceFileName && (
                <Box component="span" sx={{ ml: 1, color: "text.secondary", fontSize: 13 }}>
                  {sourceFileName}
                </Box>
              )}
            </Box>
            <TextField
              label="Content (Markdown)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              multiline
              minRows={8}
            />

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={submitting || importing || !name.trim()}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Creating..." : "Add Document"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Document created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddDocument;
